import React, { FC, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
} from '@mui/material';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EmojiTransportationOutlinedIcon from '@mui/icons-material/EmojiTransportationOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined';
import { useTranslation } from 'react-i18next';
import { SuperAdmin } from 'models/super-admin';
import DataSetSwitchIndicator from 'components/shared/data-set-switch-indicator/data-set-switch-indicator.component';

const DRAWER_WIDTH = 220;

const MENU = [
    { key: 'MENU_USERS', url: '/users', Icon: PeopleOutlineOutlinedIcon },
    { key: 'MENU_SCREEN_SHARE', url: '/screen-share', Icon: ScreenShareOutlinedIcon },
    { key: 'MENU_ORDERS', url: '/orders', Icon: ReceiptLongOutlinedIcon },
    { key: 'MENU_INVOICES', url: '/invoices', Icon: RequestQuoteOutlinedIcon },
    { key: 'MENU_CLINICS', url: '/admin-clinics', Icon: LocalHospitalOutlinedIcon },
    { key: 'MENU_LABS', url: '/admin-labs', Icon: ScienceOutlinedIcon },
    { key: 'MENU_TRANSPORT', url: '/admin-transport/transport-orders', Icon: EmojiTransportationOutlinedIcon },
    { key: 'MENU_SETTINGS', url: '/app-settings/app-banner', Icon: SettingsOutlinedIcon },
    { key: 'MENU_RELEASE_NOTES', url: '/release-notes', Icon: NewReleasesOutlinedIcon },
];

type Props = {
    admin: SuperAdmin;
    onLogout: () => void;
    children: ReactNode;
};

/** App bar plus a fixed side menu. The only navigation in the super admin UI. */
const AdminShell: FC<Props> = ({ admin, onLogout, children }) => {
    const { t: translate } = useTranslation('super-admin');

    return (
        <Box display="flex">
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {translate('TITLE')}
                    </Typography>
                    <DataSetSwitchIndicator />
                    <Typography variant="body2" sx={{ mr: 2 }}>
                        {admin.email}
                    </Typography>
                    <Button color="inherit" variant="text" onClick={onLogout} data-test="admin-shell.logout">
                        {translate('LOG_OUT')}
                    </Button>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
                }}
            >
                <Toolbar />
                <List>
                    {MENU.map(({ key, url, Icon }) => (
                        <ListItemButton
                            key={key}
                            component={NavLink}
                            to={url}
                            sx={{ '&.active': { bgcolor: 'action.selected' } }}
                        >
                            <ListItemIcon>
                                <Icon />
                            </ListItemIcon>
                            <ListItemText primary={translate(key)} />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
};

export default AdminShell;
